import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { getDocumentPatternMessage } from 'src/app/shared/utils/form-validators.util';
import {
  PASSWORD_COMPLEXITY_HINT,
  PASSWORD_COMPLEXITY_PATTERN,
} from 'src/app/shared/utils/validation.constants';

/** Misma regla de complejidad de contraseña que login / registro. */
export const HOME_PASSWORD_PATTERN = PASSWORD_COMPLEXITY_PATTERN;
export const HOME_PASSWORD_HINT = PASSWORD_COMPLEXITY_HINT;

/** Pattern de correo del registro. */
export const HOME_EMAIL_PATTERN =
  /^[a-z0-9._%+-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,4}$/;

/**
 * Valida que dos controles coincidan.
 * El error queda en el control B (confirmación) para mostrar mensaje por campo.
 */
export function matchFields(
  controlNameA: string,
  controlNameB: string,
  errorKey = 'fieldsMismatch'
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const group = control as FormGroup;
    const controlA = group.get(controlNameA);
    const controlB = group.get(controlNameB);
    if (!controlA || !controlB) {
      return null;
    }

    const valueA = (controlA.value ?? '').toString().trim();
    const valueB = (controlB.value ?? '').toString().trim();

    const clearMatchError = (): void => {
      if (!controlB.errors?.[errorKey]) {
        return;
      }
      const rest = { ...controlB.errors };
      delete rest[errorKey];
      controlB.setErrors(Object.keys(rest).length ? rest : null);
    };

    if (!valueA || !valueB) {
      clearMatchError();
      return null;
    }

    if (valueA === valueB) {
      clearMatchError();
      return null;
    }

    controlB.setErrors({ ...controlB.errors, [errorKey]: true });
    return { [errorKey]: true };
  };
}

/** True si el control fue tocado/dirty y es inválido. */
export function isFieldInvalid(
  form: AbstractControl | null | undefined,
  key: string
): boolean {
  const c = form?.get(key);
  return !!(c && (c.dirty || c.touched) && c.invalid);
}

export function dominioCorreoBloqueadoValidator(
  getDominios: () => string[]
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value == null || String(value).trim() === '') {
      return null;
    }
    const idx = String(value).lastIndexOf('@');
    if (idx <= 0 || idx === String(value).length - 1) {
      return null;
    }
    const dominio = String(value)
      .substring(idx + 1)
      .trim()
      .toLowerCase();
    const dominios = getDominios();
    if (!dominios.length) {
      return null;
    }
    return dominios.includes(dominio) ? { dominioCorreoBloqueado: true } : null;
  };
}

export function correoErrorMessage(control: AbstractControl | null): string {
  if (!control?.errors) {
    return '';
  }
  if (control.hasError('required')) {
    return 'Campo obligatorio';
  }
  if (control.hasError('dominioCorreoBloqueado')) {
    return 'No está permitido el uso de correos temporales o de dominios no válidos para el registro.';
  }
  if (control.hasError('pattern') || control.hasError('email')) {
    return 'El correo electrónico no tiene una estructura válida (ej: correo@dominio.com).';
  }
  return 'Correo electrónico no válido';
}

export function documentoErrorMessage(control: AbstractControl | null): string {
  if (!control?.errors) {
    return '';
  }
  if (control.hasError('required')) {
    return 'Campo obligatorio';
  }
  if (control.hasError('confirmDocMismatch')) {
    return 'Los números de documento no coinciden';
  }
  if (control.hasError('documentPattern')) {
    return getDocumentPatternMessage(control);
  }
  return 'Número de documento no válido';
}

export function passwordErrorMessage(control: AbstractControl | null): string {
  if (!control?.errors) {
    return '';
  }
  if (control.hasError('required')) {
    return 'Campo obligatorio';
  }
  if (control.hasError('minlength') || control.hasError('pattern')) {
    return HOME_PASSWORD_HINT;
  }
  if (control.hasError('confirmPasswordMismatch')) {
    return 'Las contraseñas no coinciden';
  }
  return 'Contraseña no válida';
}

export function celularErrorMessage(control: AbstractControl | null): string {
  if (!control?.errors) {
    return '';
  }
  if (control.hasError('required')) {
    return 'Campo obligatorio';
  }
  if (control.hasError('minlength') || control.hasError('maxlength') || control.hasError('pattern')) {
    return 'El celular debe tener 10 dígitos';
  }
  return 'Celular no válido';
}

export function requiredErrorMessage(control: AbstractControl | null): string {
  if (!control?.errors) {
    return '';
  }
  if (control.hasError('required') || control.hasError('requiredTrue')) {
    return 'Campo obligatorio';
  }
  if (control.hasError('minlength') || control.hasError('maxlength') || control.hasError('pattern')) {
    return 'El valor no cumple el formato requerido';
  }
  return 'Campo no válido';
}

export function atLeastOneSelected(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (Array.isArray(value) && value.length > 0) {
      return null;
    }
    if (value != null && value !== '') {
      return null;
    }
    return { required: true };
  };
}
